import { ConnectionManagerClient } from "./connection-manager";
import {
  API_STATUS,
  API_RESPONSE_CODES,
  CONNECTION_MANAGER_ENV,
  DEFAULT_CONNECTION_FROM,
  SOURCE,
  MAX_RETRIES,
} from "../constants/config";
import { IRequestParams, IResponse, IApiError } from "./types";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";

/**
 * Main request function for connection manager API calls
 * Handles initialization, retry logic, and error handling
 *
 * @template TResponse - Expected response type
 * @template TRequest - Request data type
 * @param params - Request parameters
 * @returns Promise with API response data
 * @throws Error if request fails after retries
 *
 * @example
 * ```typescript
 * const response = await request<OffersResponse>({
 *   operationId: OPERATION_IDS.offers,
 *   requestData: { mobileNo, sourceId: '3' },
 * });
 * ```
 */

const getPromptResponse = (response: string) => {
  try {
    return JSON.parse(response) as IResponse<unknown>;
  } catch (error) {
    throw new Error(JSON.stringify(response));
  }
};

async function initConnectionManagerLib(
  client: ConnectionManagerClient,
  source: string
): Promise<void> {
  if (source === SOURCE.APP) {
    await client.initLibAPP(source, CONNECTION_MANAGER_ENV);
  } else {
    await client.initLib(source, CONNECTION_MANAGER_ENV);
  }
}

async function callConnectionManagerApi<TResponse, TRequest>(
  client: ConnectionManagerClient,
  params: IRequestParams<TRequest>,
  source: string
): Promise<IResponse<TResponse>> {
  const {
    operationId,
    requestData,
    connectionFrom = DEFAULT_CONNECTION_FROM,
    additionalHeaders = {},
    rest = false,
  } = params;

  return rest
    ? client.callApiRest<TResponse, TRequest>(
        operationId,
        requestData,
        connectionFrom,
        additionalHeaders,
        source
      )
    : client.callApi<TResponse, TRequest>(
        operationId,
        requestData,
        connectionFrom,
        additionalHeaders,
        source
      );
}

function resolveResponse<TResponse>(response: IResponse<TResponse>): TResponse {
  if (typeof response === "string") {
    return getPromptResponse(response) as unknown as TResponse;
  }

  if (response.STATUS === API_STATUS.SUCCESS) {
    return response.API_RESPONSE as TResponse;
  }

  // Request failed with error status
  throw new Error(JSON.stringify(response));
}

function isRetryableDecryptionError(error: unknown, retryCount: number): boolean {
  const apiError = error as IApiError;

  return (
    apiError?.STATUS === API_STATUS.ERROR &&
    apiError?.API_RESPONSE?.statusCode === API_RESPONSE_CODES.STATUS_CODE_5005 &&
    retryCount < MAX_RETRIES
  );
}

function prepareRetry(operationId: string, retryCount: number): void {
  console.warn(
    `[Connection Manager] Decryption error, retrying... Attempt ${retryCount + 1}/${MAX_RETRIES}`,
    { operationId }
  );
  // Clear cached operation list data on decryption error
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("operationListData");
  }
}

export async function request<TResponse, TRequest = unknown>(
  params: IRequestParams<TRequest>
): Promise<TResponse> {
  let retryCount = 0;
  const client = ConnectionManagerClient.getInstance();

  while (retryCount <= MAX_RETRIES) {
    try {
      const source = isOpenInApp() ? SOURCE.APP : SOURCE.WEB;

      await initConnectionManagerLib(client, source);

      const response = await callConnectionManagerApi<TResponse, TRequest>(client, params, source);

      return resolveResponse<TResponse>(response);
    } catch (error: unknown) {
      if (!isRetryableDecryptionError(error, retryCount)) {
        throw error;
      }

      prepareRetry(params.operationId, retryCount);
      retryCount++;
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new Error(
    `[Connection Manager] Max retries (${MAX_RETRIES}) exceeded for operation: ${params.operationId}`
  );
}

/**
 * Request with custom error handling
 * Returns null on error instead of throwing
 *
 * @template TResponse - Expected response type
 * @template TRequest - Request data type
 * @param params - Request parameters
 * @returns Promise with API response data or null on error
 *
 * @example
 * ```typescript
 * const response = await requestSafe<OffersResponse>({
 *   operationId: OPERATION_IDS.offers,
 *   requestData: { mobileNo, sourceId: '3' },
 * });
 *
 * if (response === null) {
 *   // Handle error
 * }
 * ```
 */
export async function requestSafe<TResponse, TRequest = unknown>(
  params: IRequestParams<TRequest>
): Promise<TResponse | null> {
  try {
    return await request<TResponse, TRequest>(params);
  } catch (error) {
    console.error(
      `[Connection Manager] Request failed for operation: ${params.operationId}`,
      error
    );
    return null;
  }
}
